import { Injectable } from '@angular/core';

import { WysiwygLink, WysiwygLinkType } from '../../../wysiwyg-extensions/link/wysiwyg-link.types';
import { Redactor, RedactorApp, RedactorComponentType, RedactorConfig } from '../../redactor.types';
import { Wrapper } from '../interfaces';
import { ANGULAR_ADAPTER_PLUGIN_TOKEN } from '../redactor-adapter';
import { mergeRedactorConfigArrField, RedactorExtension } from '../redactor-extension';

declare let $R: Redactor;

// plugin constants
export const LINK_PLUGIN_TOKEN = 'rt-link';
export const LINK_ANGULAR_COMPONENT = 'rt-wysiwyg-link';

// redactor stuff

const installRtLinkExtension = () => {
  $R.add('plugin', LINK_PLUGIN_TOKEN, {
    init(app: RedactorApp): void {
      this.app = app;
      this.editor = app.editor;
      this.cleaner = app.cleaner;
      this.opts = app.opts;
      this.component = app.component;
      this.utils = app.utils;
      this.insertion = app.insertion;
      this.inspector = app.inspector;
      this.caret = app.caret;
      this.selection = app.selection;
      this.keycodes = app.keycodes;
      this.adapter = app.plugin[ANGULAR_ADAPTER_PLUGIN_TOKEN];

      this.linksData = {
        [WysiwygLinkType.Scheduler]: {},
        [WysiwygLinkType.Questionnaires]: {},
      };

      const matchers: { [key in WysiwygLinkType]?: (test: string) => boolean } = {
        [WysiwygLinkType.Questionnaires]: str => /v\/i\/t/.test(str),
        [WysiwygLinkType.Scheduler]: str => /v\/i\/s/.test(str),
        [WysiwygLinkType.External]: _ => false,
      };

      this.matchers = matchers;
      this.matcherKeys = Object.keys(matchers);
    },
    start(): void {
      // TODO: convert GDPR [gdpr_request_removal_url] [gdpr_request_change_url] tags

      this.cleaner.addConvertRules(LINK_PLUGIN_TOKEN, ($wrapper: Wrapper) => {
        let $$wrapper = $wrapper;
        Array.from($$wrapper.find('a').nodes)
          // Only links with href same as text should be inserted as WysiwygLink
          .filter(node => this.linkNotCustomized(node))
          .map((node: HTMLElement) => this.getLinkFromNode(node))
          .filter(({ link }) => link.type !== undefined)
          .forEach(({ link, node }) => $R.dom(node).replaceWith(this.createComponent(link)));

        if (this.opts.rtLinksExcludeParentSelector) {
          $$wrapper = $wrapper
            .find(
              `${this.opts.rtLinksExcludeParentSelector} [data-angular-type="${LINK_ANGULAR_COMPONENT}"]`,
            )
            .replaceWith(node => $R.dom(decodeURI(node.dataset['interpolatedValue'])));
        }

        return $$wrapper;
      });

      this.cleaner.addUnconvertRules(LINK_PLUGIN_TOKEN, ($wrapper: Wrapper) => {
        // console.time('link_unconvert');

        $wrapper
          .find(`[data-angular-type="${LINK_ANGULAR_COMPONENT}"]`)
          .replaceWith(node => $R.dom(decodeURI(node.dataset['interpolatedValue'])));

        // console.timeEnd('link_unconvert');
        return $wrapper;
      });
    },
    stop(): void {
      delete this.cleaner.convertRules[LINK_PLUGIN_TOKEN];
      delete this.cleaner.unconvertRules[LINK_PLUGIN_TOKEN];
    },
    insert(link: WysiwygLink): void {
      this.selection.restore();
      if (this.isAnchorLink(link)) {
        this.insertion.insertNode(
          $R.dom(
            `<a href="${link.url}" target="${link.target || 'self'}">${link.name || link.url}</a>`,
          ),
        );
      } else {
        this.insertion.insertNode(this.createComponent(link));
      }
    },
    getLinkFromNode(node: HTMLAnchorElement): { node: HTMLAnchorElement; link: WysiwygLink } {
      const url = node.getAttribute('href')!;
      const type = this.extractTypeFromUrl(url);

      if (!type) {
        return {
          node,
          link: {
            type: undefined,
            url: undefined,
          },
        };
      }

      const id = type ? this.readIdFromAnchorNode(node).id : undefined;
      const name = (type && this.getLinkName(type, id)) || false;

      return {
        node,
        link: {
          url,
          type,
          id,
          name: name || node.innerText,
          expired: name === false,
        },
      };
    },
    linkNotCustomized(node: HTMLAnchorElement): boolean {
      return node.innerText === node.href;
    },
    readIdFromAnchorNode(anchor: HTMLAnchorElement): {
      type: WysiwygLinkType | undefined;
      id: string | false;
    } {
      const questionnaireMatch = anchor.href.match(/.*\/t\/(.*)\/(.*)/);

      if (questionnaireMatch) {
        return {
          type: WysiwygLinkType.Questionnaires,
          id: Array.isArray(questionnaireMatch) ? questionnaireMatch[1] : false,
        };
      }

      const schedulerMatch = anchor.href.match(/.*\/s\/(.*)\/(.*)/);

      if (schedulerMatch) {
        return {
          type: WysiwygLinkType.Scheduler,
          id: Array.isArray(schedulerMatch) ? schedulerMatch[1] : false,
        };
      }
      return {
        type: undefined,
        id: WysiwygLinkType.External,
      };
    },
    getLinkName(type: WysiwygLinkType, id: string): string {
      return this.linksData[type][id];
    },
    extractTypeFromUrl(url: string): WysiwygLinkType | undefined {
      return <WysiwygLinkType | undefined>(
        this.matcherKeys.find((key: string) => this.matchers[key](url))
      );
    },
    updateLinksData(linksData: {
      [WysiwygLinkType.Scheduler]: { [key: number]: string };
      [WysiwygLinkType.Questionnaires]: { [key: number]: string };
    }): void {
      this.linksData = linksData;
      const $linkRawCmpElements = this.editor.getElement().find('rt-wysiwyg-link');
      $linkRawCmpElements.each(($linkCmpElement: HTMLElement) => {
        const state = this.adapter.getHostData($linkCmpElement.id);

        // eslint-disable-next-line default-case
        switch (state?.type) {
          case WysiwygLinkType.Scheduler:
          case WysiwygLinkType.Questionnaires:
            const $adapterCmp = this.component.build($R.dom($linkCmpElement).parent().get());

            const name = this.getLinkName(state.type, state.id) || false;
            const nextState = {
              ...state,
              name: name || state.name,
              expired: name === false,
            };

            $adapterCmp.pushDataToAngularComponent(
              nextState,
              this.createAnchorElement(nextState).outerHTML,
            );
        }
      });
    },
    createComponent(link: WysiwygLink): RedactorComponentType {
      const cmp$ = this.component.create(ANGULAR_ADAPTER_PLUGIN_TOKEN, {
        angularTagName: LINK_ANGULAR_COMPONENT,
        data: link,
      });

      cmp$.writeValue(this.createAnchorElement(link).outerHTML);

      return cmp$;
    },
    createAnchorElement(link: WysiwygLink): HTMLAnchorElement {
      const anchor = document.createElement('a');
      anchor.href = link.url!;
      anchor.innerText = link.url!;
      anchor.target = link.target || 'self';
      return anchor;
    },
    isAnchorLink(link: WysiwygLink): boolean {
      return [
        WysiwygLinkType.GdprChange,
        WysiwygLinkType.GdprRemove,
        WysiwygLinkType.GdprConsentRequest,
        WysiwygLinkType.IntegrationsKununuApplicantFeedback,
        WysiwygLinkType.IntegrationsKununuEmployeeFeedback,
      ].includes(link.type!);
    },
  });
};

@Injectable({
  providedIn: 'root',
})
export class RedactorLinkExtension extends RedactorExtension {
  constructor() {
    super();

    if (!$R.classes[LINK_PLUGIN_TOKEN]) {
      installRtLinkExtension();
    }
  }

  public canInstall(config: RedactorConfig): boolean {
    return config.plugins!.includes(LINK_PLUGIN_TOKEN);
  }

  public install(config: RedactorConfig): RedactorConfig {
    this.enable();

    return {
      ...config,
      plugins: mergeRedactorConfigArrField(config, 'plugins', [
        'variable',
        ANGULAR_ADAPTER_PLUGIN_TOKEN,
      ]),
    };
  }
}
